import { IImage, IGif } from '@giphy/js-types';
import { PersonHelpers } from '@frontend/api-person';
import type { TeamChatTypes } from '@frontend/api-team-chat';
import { StreamGiphyType, GiphyVersionInfo } from '@frontend/gif-picker';

export const getUserFullName = (
  firstName: TeamChatTypes.User['firstName'],
  lastName: TeamChatTypes.User['lastName']
) => {
  return PersonHelpers.getFullName({
    FirstName: firstName,
    LastName: lastName,
  });
};

/**
 * Extracts an array of matching usernames from text by finding @ mentions.
 *
 * Algorithm:
 * 1. Finds each @ symbol in the text
 * 2. For each @, tries to match against provided usernames (longest first to prioritize full names over partials)
 * 3. Uses word boundaries to ensure exact matches and avoid false positives
 * 4. Deduplicates results automatically
 *
 * Examples:
 * - "@john help @jane" with ['john', 'jane'] → ['john', 'jane']
 * - "@john doe works with @jane" with ['john', 'john doe', 'jane'] → ['john doe', 'jane']
 *   (longer "john doe" match prioritized over partial "john")
 * - "@john works @john again" with ['john'] → ['john'] (deduplicated)
 * - "@alice smith" with ['alice smith', 'alice'] → ['alice smith'] (handles multi-word names)
 *
 * Handles:
 * - Multiple mentions of the same user (auto-deduplicated)
 * - Multi-word usernames with flexible spacing
 * - Special characters: underscores (_), hyphens (-), plus (+), apostrophes ('), periods (.)
 * - Case-sensitive matching (preserves original username casing)
 * - Word boundaries to stop at natural text breaks
 */
export const getMentionsFromText = (text: string, usernames: string[]): string[] => {
  const mentions = new Set<string>();

  if (!usernames.length) return [];

  // Sort usernames by normalized length (longest first) to prioritize full matches
  const sortedUsernames = [...usernames].sort(
    (a, b) => b.replaceAll(/\s/g, '').length - a.replaceAll(/\s/g, '').length
  );

  // Find all @ symbols and try to match usernames at each position
  let atIndex = 0;
  while ((atIndex = text.indexOf('@', atIndex)) !== -1) {
    const afterAt = text.substring(atIndex + 1);

    // Try to match each username (longest first)
    for (const username of sortedUsernames) {
      // Split username by spaces and escape regex special characters
      const parts = username.split(/\s+/).map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      const pattern = parts.join('\\s+');

      // Match username followed by word boundary
      const regex = new RegExp(`^${pattern}(?=\\s|$|[^a-zA-Z0-9_+.'-])`);

      if (regex.test(afterAt)) {
        mentions.add(username);
        break;
      }
    }

    atIndex += 1;
  }

  return Array.from(mentions);
};

/**
 *
 * Extracts an array of matching usernames from the text
 */
export const getChannelsFromText = (text: string, channels: string[]) => {
  const chars = text.replaceAll(/\s/g, '');
  return channels.filter((channel) => chars.includes('#' + channel.replaceAll(/\s/g, '')));
};

export type Target =
  | {
      type: 'dm';
      users: TeamChatTypes.User[];
    }
  | {
      type: 'group';
      conversation: TeamChatTypes.Conversation;
    };

// This function converts value of object to string. When gif is attached the value might be number,
// so we convert it to string
const convertValuesToString = (input: IImage): GiphyVersionInfo => {
  return {
    height: String(input.height),
    url: input.url,
    width: String(input.width),
    // Since input is a optional field that is why adding a check is size is in the object
    ...(input.size && { size: String(input.size) }),
  };
};

export const parseGiphyToStreamGifAttachment = (gif: IGif): StreamGiphyType => {
  const giphy: StreamGiphyType['giphy'] = {
    original: convertValuesToString(gif.images.original),
    fixed_height: convertValuesToString(gif.images.fixed_height),
    fixed_height_still: convertValuesToString(gif.images.fixed_height_still),
    fixed_height_downsampled: convertValuesToString(gif.images.fixed_height_downsampled),
    fixed_width: convertValuesToString(gif.images.fixed_width),
    fixed_width_still: convertValuesToString(gif.images.fixed_width_still),
    fixed_width_downsampled: convertValuesToString(gif.images.fixed_width_downsampled),
  };

  return {
    title: gif.title,
    title_link: gif.url,
    thumb_url: gif.images.original.url,
    giphy,
  };
};

type Pages = { rows: TeamChatTypes.Thread[]; next: string | undefined }[];

export const cascadeThreadMessageUp = (
  pages: Pages,
  thread: TeamChatTypes.Thread & { pageIndex: number; rowIndex: number },
  updatedThread: TeamChatTypes.Thread
): Pages => {
  const newPages = [...pages];
  const { pageIndex: threadPageIndex, rowIndex } = thread;

  if (threadPageIndex === 0 && rowIndex === 0) {
    pages[0].rows[0] = updatedThread;
    return pages;
  }
  // Remove the thread from its current position and shift items

  // Remove thread from its current position
  newPages[threadPageIndex] = {
    ...newPages[threadPageIndex],
    rows: newPages[threadPageIndex].rows.filter((_, index) => index !== rowIndex),
  };

  // Handle cross-page movement vs same-page movement differently
  if (threadPageIndex === 0) {
    // Same page movement - just add to top, no cascading needed
    newPages[0] = {
      ...newPages[0],
      rows: [updatedThread, ...newPages[0].rows],
    };
  } else {
    // Cross-page movement - need to cascade items down
    let itemToShift = null;

    // Start from the first page and cascade items down
    for (let pageIndex = 0; pageIndex <= threadPageIndex; pageIndex++) {
      const currentPage = newPages[pageIndex];

      if (pageIndex === 0) {
        // For first page, take the last item to cascade down
        if (currentPage.rows.length > 0) {
          itemToShift = currentPage.rows[currentPage.rows.length - 1];
          newPages[0] = {
            ...currentPage,
            rows: currentPage.rows.slice(0, -1), // Remove last item
          };
        }
      } else {
        // For subsequent pages, add the cascading item to the beginning
        // and take the last item to cascade to next page
        if (itemToShift) {
          const nextItemToShift = currentPage.rows.length > 0 ? currentPage.rows[currentPage.rows.length - 1] : null;
          newPages[pageIndex] = {
            ...currentPage,
            rows: currentPage.rows.length > 0 ? [itemToShift, ...currentPage.rows.slice(0, -1)] : [itemToShift],
          };
          itemToShift = nextItemToShift;
        }
      }
    }

    // Add the final cascaded item back to the last page (the one where thread was removed)
    if (itemToShift) {
      newPages[threadPageIndex] = {
        ...newPages[threadPageIndex],
        rows: [...newPages[threadPageIndex].rows, itemToShift],
      };
    }

    // Add updated thread to top of first page
    newPages[0] = {
      ...newPages[0],
      rows: [updatedThread, ...newPages[0].rows],
    };
  }

  return newPages;
};
